/**
 * People & roles — users, invitations, role/permission grants (doc 01 A6
 * People & roles, doc 13 T9/T10/T11). Mirrors the `/users`, `/invitations`, and
 * `/users/:id/grants` route family verbatim.
 *
 * Permission model (enforced server-side at the route boundary):
 *   - reads  → `user:read`
 *   - account lifecycle + invitations → `user:manage`
 *   - role/permission grants → `role:assign`
 *
 * No token or secret is ever returned by an admin call here: the invitation
 * create/resend routes deliberately discard the invite token (delivered
 * out-of-band, never echoed to the browser).
 */
import { del, get, post, putBinary } from './transport.js';
import { personPhotoSrc } from './photos.js';
/** `GET /users` → `{ users }` (needs `user:read`). */
export async function list(options) {
    const body = await get('/users', options);
    return body.users;
}
/** `GET /roles` → `{ roles }` (needs `user:read`). */
export async function roles(options) {
    const body = await get('/roles', options);
    return body.roles;
}
/**
 * MAY AN ADMINISTRATOR HAND THIS KEY OUT — the only safe way to ask.
 *
 * Default-deny, and deliberately not the negation of `reserved`: a metadata
 * entry the server did not describe, or one from a server older than F418,
 * answers `false` here rather than being waved through. `KNOWN_PERMISSION !=
 * ORG_ADMIN_GRANTABLE_PERMISSION`, and a UI that offers a control the grant
 * guard will refuse has told an administrator they have an authority they do
 * not have.
 *
 * A screen that wants to SAY WHY should read `grantability` and
 * `reservedReason` directly: `false` here covers both "reserved" and "the
 * server did not say", which are different sentences.
 *
 * WHY A SWITCH AND NOT THE ONE-LINE EQUALITY IT REPLACES (L132). The equality
 * was already default-deny and stays exactly as correct at runtime; what it did
 * not do was NOTICE. `PermissionGrantability` here is a hand-kept mirror of the
 * server's, and the day the server grows a third state somebody has to widen
 * this union — at which point `state` in the `default` branch stops being
 * `never` and this package stops compiling, which is the reminder to come and
 * decide what the new state means to a screen. An expression cannot be
 * exhaustive over anything, so a one-liner can only ever be silently right.
 */
export function isGrantable(meta) {
    const state = meta?.grantability;
    switch (state) {
        case 'grantable':
            return true;
        case 'reserved':
            return false;
        // The server did not say. A third state, and not `grantable`.
        case undefined:
            return false;
        default:
            return refuseUnrecognizedGrantability(state);
    }
}
/**
 * A grantability state this bundle does not know is NOT grantable.
 *
 * `state: never` makes widening `PermissionGrantability` a build error at the
 * only call site; the `false` is the answer for the case the compiler cannot
 * see — a bundle older than the server that answered it, which is the exact
 * skew `grantability?` is optional for.
 */
function refuseUnrecognizedGrantability(state) {
    void state;
    return false;
}
/**
 * `GET /roles/overview` → `{ overview }` (needs `user:read`).
 *
 * A read of the real authorization system: roles with member counts and
 * permission keys, the live catalog, and the human-readable metadata layer.
 * Role mutations stay on the existing grant routes (`role:assign`).
 */
export async function rolesOverview(options) {
    const body = await get('/roles/overview', options);
    return body.overview;
}
/** `GET /users/:id` → `{ user }` (needs `user:read`). */
export async function person(id, options) {
    const body = await get(`/users/${id}`, options);
    return body.user;
}
/** `POST /users/:id/suspend` (needs `user:manage`). */
export async function suspend(id, options) {
    await post(`/users/${id}/suspend`, undefined, options);
}
/** `POST /users/:id/deactivate` (needs `user:manage`). */
export async function deactivate(id, options) {
    await post(`/users/${id}/deactivate`, undefined, options);
}
/** `POST /users/:id/reactivate` (needs `user:manage`; suspended → active only). */
export async function reactivate(id, options) {
    await post(`/users/${id}/reactivate`, undefined, options);
}
/** `GET /users/:id/grants` → `{ grants }` (needs `role:assign`). */
export async function grants(id, options) {
    const body = await get(`/users/${id}/grants`, options);
    return body.grants;
}
/** `POST /users/:id/roles` → `{ grant }` (needs `role:assign`). */
export async function assignRole(id, input, options) {
    const body = await post(`/users/${id}/roles`, input, options);
    return body.grant;
}
/** `DELETE /users/:id/roles/:grantId` (needs `role:assign`). */
export async function removeRole(id, grantId, options) {
    await del(`/users/${id}/roles/${grantId}`, options);
}
/** `POST /users/:id/permissions` (needs `role:assign`). */
export async function grantPermission(id, input, options) {
    await post(`/users/${id}/permissions`, input, options);
}
/** `DELETE /users/:id/permissions/:grantId` (needs `role:assign`). */
export async function revokePermission(id, grantId, options) {
    await del(`/users/${id}/permissions/${grantId}`, options);
}
/** `GET /invitations` → `{ invitations }` (needs `user:read`). */
export async function invitations(options) {
    const body = await get('/invitations', options);
    return body.invitations;
}
/**
 * `POST /invitations` → {@link InviteResult} (needs `user:manage`). Token is
 * never returned.
 */
export async function invite(input, options) {
    return post('/invitations', input, options);
}
/** `POST /invitations/:id/revoke` (needs `user:manage`). */
export async function revokeInvitation(id, options) {
    await post(`/invitations/${id}/revoke`, undefined, options);
}
/**
 * `POST /invitations/:id/resend` → {@link ResendResult} (needs `user:manage`).
 * Token is never returned, and neither is the address.
 *
 * The SAME two facts as {@link invite}, and for the same reason (F105). This
 * used to answer `delivery` alone — the exact shape F94 removed from create —
 * so a caller could render 'queued' as a send on a deployment with no mail
 * credential. Resend is the button someone presses BECAUSE the first attempt
 * did not arrive, so telling them the same untrue thing a second time is the
 * worse version of the same defect.
 */
export async function resendInvitation(id, options) {
    return post(`/invitations/${id}/resend`, undefined, options);
}
/**
 * `POST /invitations/:id/resend` with a CORRECTED address (F95) — needs
 * `user:manage` AND `role:assign`, the same pair {@link invite} needs.
 *
 * A separate function from {@link resendInvitation} on purpose. The two are one
 * route and two capabilities: resending grants nothing new, while repointing an
 * invitation chooses which mailbox may redeem the role it names, which is why
 * the server asks for a second key. Folding the address into an optional
 * parameter would have made the more powerful call the easier one to reach by
 * accident.
 *
 * Server-side this is ONE statement: the address is corrected and the token
 * rotated together, so a link already sitting in the mistyped mailbox is dead
 * before this resolves. The response is unchanged — the same two operational
 * facts, no address, no token. `409` when the corrected address already has a
 * pending invitation, or when it is the caller's own.
 */
export async function resendInvitationToCorrectedAddress(id, email, options) {
    return post(`/invitations/${id}/resend`, { email }, options);
}
/**
 * The serving URL for a person's profile photo, for callers that have already
 * decided a photo exists.
 *
 * NEW CODE SHOULD CALL `photos.personPhotoSrc(person)` INSTEAD. It is the one
 * canonical read contract (see photos.ts and docs/PROFILE-PHOTO-CONTRACT.md),
 * it answers null for "no photo" rather than handing back a URL that will 404,
 * and it takes the person shape a projection already has. This function remains
 * for existing call sites and now delegates to it, so there is exactly one
 * place that knows how the URL is built.
 */
export function photoUrl(id, version) {
    return personPhotoSrc({ userId: id, photoUpdatedAt: version ?? undefined }) ?? '';
}
/**
 * `PUT /users/:id/photo` — raw image bytes (jpeg/png/webp, ≤5 MB). Self-service
 * for one's own photo; `user:manage` for anyone else's. The server re-derives
 * the type from the bytes and refuses mismatches, so the declared type must be
 * the file's real one.
 */
export async function uploadPhoto(id, file, options) {
    return putBinary(`/users/${id}/photo`, file, file.type, options);
}
/** `DELETE /users/:id/photo` — remove own photo, or anyone's with `user:manage`. */
export async function removePhoto(id, options) {
    await del(`/users/${id}/photo`, options);
}
//# sourceMappingURL=users.js.map